// Buscador de "Lugar de expedición": municipios de Colombia (DIVIPOLA del DANE,
// servidos por GET /municipalities) o texto libre sin números si el documento
// se expidió fuera de Colombia. El valor ya guardado se conserva tal cual
// mientras no se elija otro: solo cambia cuando se selecciona explícitamente.
import React from 'react';
import { Box, Checkbox, Input, Spinner, Text } from '@chakra-ui/react';
import xhr from 'xhr';

const HAS_DIGITS = /\d/;
// Un valor elegido de la lista tiene la forma "Municipio, Departamento" y no lleva números.
const looksLikeListValue = (value) => !HAS_DIGITS.test(value) && value.includes(',');

export default function MunicipalityInput({
  value = '',
  initialValue = '',
  onChange,
  placeholder = 'Busca el municipio',
}) {
  const [query, setQuery] = React.useState(value);
  const [options, setOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [foreign, setForeign] = React.useState(false);
  const [foreignError, setForeignError] = React.useState('');

  // Si el formulario recarga el valor (por ejemplo al guardar), se refleja en el campo.
  React.useEffect(() => {
    setQuery(value);
  }, [value]);

  React.useEffect(() => {
    if (foreign || !open) return undefined;
    const term = query.trim();
    if (term.length < 2 || term === value) {
      setOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await xhr.get('/municipalities', { params: { q: term, limit: 12 } });
        setOptions(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error(error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, foreign, value]);

  const choose = (option) => {
    onChange(option.label);
    setQuery(option.label);
    setOpen(false);
  };

  const handleBlur = () => {
    // Si escribió pero no eligió una opción, se mantiene el valor que había.
    setTimeout(() => {
      setOpen(false);
      setQuery(value);
    }, 150);
  };

  const handleForeignText = (text) => {
    setQuery(text);
    // Se pasa el texto tal cual: el formulario no deja guardarlo si trae números.
    setForeignError(HAS_DIGITS.test(text) ? 'Escribe solo el lugar (ciudad y país), sin fechas ni números.' : '');
    onChange(text.trim());
  };

  const toggleForeign = (checked) => {
    setForeign(checked);
    setForeignError('');
    setOptions([]);
    setOpen(false);
    setQuery(checked ? '' : value);
  };

  const keepsLegacyValue = Boolean(initialValue) && value === initialValue && !looksLikeListValue(initialValue);

  return (
    <Box>
      {/* La lista se ancla a este contenedor para que abra justo debajo del campo. */}
      <Box position="relative">
        {foreign ? (
          <Input
            placeholder="Ciudad y país de expedición"
            value={query}
            onChange={(e) => handleForeignText(e.target.value)}
            isInvalid={Boolean(foreignError)}
          />
        ) : (
          <Input
            placeholder={placeholder}
            value={query}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onBlur={handleBlur}
          />
        )}

        {!foreign && open && (loading || options.length > 0) && (
          <Box
            position="absolute"
            zIndex={20}
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="md"
            maxH="240px"
            overflowY="auto"
            role="listbox"
          >
            {loading && (
              <Box px={3} py={2}>
                <Spinner size="sm" color="purple.500" />
              </Box>
            )}
            {options.map((option) => (
              <Box
                key={option.code}
                role="option"
                px={3}
                py={2}
                cursor="pointer"
                fontSize="sm"
                _hover={{ bg: 'purple.50' }}
                // onMouseDown para que la selección ocurra antes del blur del input.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(option);
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {!foreign && open && query.trim() !== value && query.trim().length >= 2 && !loading && options.length === 0 && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          No encontramos ese municipio. Revisa cómo está escrito.
        </Text>
      )}
      {foreignError && (
        <Text fontSize="xs" color="red.500" mt={1}>
          {foreignError}
        </Text>
      )}
      {keepsLegacyValue && !foreign && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          Valor registrado anteriormente. Se conserva mientras no elijas un municipio de la lista.
        </Text>
      )}
      <Checkbox
        mt={2}
        size="sm"
        colorScheme="purple"
        isChecked={foreign}
        onChange={(e) => toggleForeign(e.target.checked)}
      >
        Expedido fuera de Colombia
      </Checkbox>
    </Box>
  );
}
